import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, FormError, Input } from "@/components/ui/field";
import { ConfigPass } from "@/features/configs/config-pass";
import { ConfigSheet } from "@/features/configs/config-sheet";
import { FormatPicker } from "@/features/configs/format-picker";
import { DeleteDialog, ReissueDialog } from "@/features/configs/manage-dialogs";
import { type SwipeAction, SwipeActions } from "@/features/configs/swipe-actions";
import { TrafficChart, TrafficTiles } from "@/features/traffic/traffic";
import type { Schemas } from "@/lib/api/client";
import {
  type Config,
  type ConfigFormat,
  configsQuery,
  type MetricsRange,
  metaQuery,
  myTrafficQuery,
  useCreateConfig,
  useRequestConfig,
} from "@/lib/api/queries";
import { formatRelative, plural } from "@/lib/format";

export const Route = createFileRoute("/_app/")({
  validateSearch: z.object({ welcome: z.boolean().optional(), config: z.number().optional() }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(metaQuery);
    return context.queryClient.ensureQueryData(configsQuery);
  },
  component: HomePage,
});

function HomePage() {
  const { data } = useSuspenseQuery(configsQuery);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [creating, setCreating] = useState(false);
  const [printed, setPrinted] = useState<number | null>(null);
  const [swiped, setSwiped] = useState<number | null>(null);
  const [managing, setManaging] = useState<{ kind: "reissue" | "delete"; config: Config }>();
  const meta = useQuery(metaQuery);

  // After registration, open the first config right away: that is what the person came for.
  const openId = search.config ?? (search.welcome ? data.items[0]?.id : undefined);
  const openConfig = data.items.find((c) => c.id === openId);
  const setOpen = (id: number | undefined) =>
    navigate({ search: id === undefined ? {} : { config: id }, replace: true });

  const canCreate = data.self_configs_used < data.self_config_limit;

  return (
    <main className="mx-auto max-w-xl px-4 pt-8 pb-16">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl">Мои конфиги</h1>
          <p className="mt-1 text-sm text-muted">
            Своих {data.self_configs_used} из {data.self_config_limit}
          </p>
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden />
            Новый
          </Button>
        )}
      </div>

      {data.items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-line p-8 text-center">
          <p>Конфигов пока нет.</p>
          {canCreate && (
            <Button className="mt-4" onClick={() => setCreating(true)}>
              Создать конфиг
            </Button>
          )}
        </div>
      ) : (
        <>
          <ul className="mt-6 grid gap-4">
            <AnimatePresence initial={false}>
              {data.items.map((config, index) => (
                <motion.li
                  key={config.id}
                  layout
                  // The one orchestrated moment: a freshly issued pass slides out like a printed ticket.
                  initial={
                    config.id === printed
                      ? { clipPath: "inset(0 0 100% 0 round 1rem)", y: -12 }
                      : false
                  }
                  animate={{ clipPath: "inset(0 0 0% 0 round 1rem)", y: 0 }}
                  exit={{ opacity: 0, x: 160, transition: { duration: 0.25 } }}
                  transition={{
                    duration: 0.6,
                    ease: [0.2, 0.8, 0.2, 1],
                    layout: { duration: 0.3 },
                  }}
                >
                  <SwipeActions
                    actions={swipeActions(config, (kind) => setManaging({ kind, config }))}
                    open={swiped === config.id}
                    onOpenChange={(open) =>
                      setSwiped((current) =>
                        open ? config.id : current === config.id ? null : current,
                      )
                    }
                  >
                    <ConfigPass
                      config={config}
                      number={index + 1}
                      onOpen={() => setOpen(config.id)}
                    />
                  </SwipeActions>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          <p className="mt-3 text-center text-xs text-muted">
            Смахните пропуск вправо, чтобы перевыпустить или удалить его
          </p>
        </>
      )}

      <MoreConfigs
        limit={data.self_config_limit}
        canCreate={canCreate}
        lastRequest={data.last_request}
      />

      {data.items.length > 0 && <MyTraffic configs={data.items} />}

      <ConfigSheet config={openConfig} onOpenChange={(open) => !open && setOpen(undefined)} />
      {managing?.kind === "reissue" && (
        <ReissueDialog
          key={managing.config.id}
          config={managing.config}
          open
          onOpenChange={(open) => !open && setManaging(undefined)}
          cooldownHours={meta.data?.reissue_cooldown_hours}
        />
      )}
      {managing?.kind === "delete" && (
        <DeleteDialog
          key={managing.config.id}
          config={managing.config}
          open
          onOpenChange={(open) => !open && setManaging(undefined)}
        />
      )}
      <CreateConfigDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => {
          setPrinted(id);
          setOpen(id);
        }}
      />
    </main>
  );
}

function MyTraffic({ configs }: { configs: Config[] }) {
  const [range, setRange] = useState<MetricsRange>("7d");
  const { data } = useQuery({ ...myTrafficQuery(range), placeholderData: keepPreviousData });
  if (!data) return null;
  return (
    <section className="mt-10">
      <h2 className="text-base">Трафик</h2>
      <TrafficTiles totals={data.totals} configs={configs} className="mt-3" />
      <TrafficChart data={data.series} range={range} onRangeChange={setRange} className="mt-4" />
    </section>
  );
}

type ConfigRequest = NonNullable<Schemas["ConfigsOut"]["last_request"]>;

/** Past the limit: ask the admin for one more, and see how the last request went. */
function MoreConfigs({
  limit,
  canCreate,
  lastRequest,
}: {
  limit: number;
  canCreate: boolean;
  lastRequest: ConfigRequest | null;
}) {
  const [asking, setAsking] = useState(false);
  if (canCreate) {
    const freshlyApproved =
      lastRequest?.status === "approved" &&
      lastRequest.resolved_at !== null &&
      Date.now() - new Date(lastRequest.resolved_at).getTime() < 7 * 86_400_000;
    return freshlyApproved ? (
      <p className="mt-6 text-sm text-online">
        Администратор одобрил запрос — можно создать ещё один конфиг.
      </p>
    ) : null;
  }
  const pending = lastRequest?.status === "pending";

  return (
    <div className="mt-6 rounded-2xl border border-line bg-surface p-4 text-sm">
      <p className="text-muted">
        Вы используете все {limit}{" "}
        {plural(limit, ["свой конфиг", "своих конфига", "своих конфигов"])}.
      </p>
      {pending ? (
        <p className="mt-2">
          Запрос на ещё один отправлен {formatRelative(lastRequest.created_at)}. Ждём
          администратора.
        </p>
      ) : (
        <>
          {lastRequest?.status === "rejected" && (
            <p className="mt-2">
              Прошлый запрос отклонён
              {lastRequest.admin_comment ? `: «${lastRequest.admin_comment}»` : "."}
            </p>
          )}
          <Button size="sm" variant="secondary" className="mt-3" onClick={() => setAsking(true)}>
            Попросить ещё один
          </Button>
        </>
      )}
      <RequestConfigDialog open={asking} onOpenChange={setAsking} />
    </div>
  );
}

function RequestConfigDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const request = useRequestConfig();
  const [comment, setComment] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Ещё один конфиг"
      description="Администратор увидит запрос и поднимет лимит. После этого конфиг создаёте вы сами."
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          request.mutate(comment.trim() || null, {
            onSuccess: () => {
              toast.success("Запрос отправлен");
              onOpenChange(false);
              setComment("");
            },
          });
        }}
      >
        <Field label="Для чего" hint="Необязательно. Например, «для планшета»">
          {(control) => (
            <Input
              {...control}
              value={comment}
              maxLength={300}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Field>
        <FormError message={request.error?.message} />
        <Button type="submit" pending={request.isPending}>
          Отправить
        </Button>
      </form>
    </Dialog>
  );
}

function swipeActions(config: Config, manage: (kind: "reissue" | "delete") => void): SwipeAction[] {
  // A suspended config belongs to the admin's decision: nothing to do with it from here.
  if (config.status === "suspended") return [];
  const remove: SwipeAction = {
    label: "Удалить",
    icon: Trash2,
    danger: true,
    onSelect: () => manage("delete"),
  };
  if (config.status !== "active") return [remove];
  return [{ label: "Перевыпуск", icon: RefreshCw, onSelect: () => manage("reissue") }, remove];
}

function CreateConfigDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: number) => void;
}) {
  const create = useCreateConfig();
  const [format, setFormat] = useState<ConfigFormat>("amneziawg");
  const [name, setName] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Новый конфиг"
      description="Для ещё одного устройства. Название видите только вы и администратор."
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate(
            { format, name: name.trim() || null },
            {
              onSuccess: (config) => {
                toast.success("Конфиг готов");
                onOpenChange(false);
                setName("");
                onCreated(config.id);
              },
            },
          );
        }}
      >
        <Field label="Название" hint="Необязательно. Например, «Ноутбук»">
          {(control) => (
            <Input
              {...control}
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <FormatPicker value={format} onChange={setFormat} name="new-format" />
        <FormError message={create.error?.message} />
        <Button type="submit" pending={create.isPending}>
          Создать
        </Button>
      </form>
    </Dialog>
  );
}
